import React, {useState, useCallback} from 'react'
import PropTypes from 'prop-types'

import {Box, Text, Button} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import {READ_MORE_LABEL} from '@app/constants'

/**
 * A React component that conditionally displays a short description with a "Read more" button
 * to expand and reveal the full description. This component is useful for displaying lengthy
 * text content in a compact form, allowing users to expand it if they wish to read more.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {string} props.description - The full description text to be displayed when expanded.
 * @param {string} [props.shortDescription] - The short version of the description to be displayed initially.
 * @param {Object} [props.stylesProps] - Additional style properties to be applied to the container.
 * @returns {JSX.Element|null} A JSX element containing the description text, or null if no description is provided.
 */
const ReadMoreText = ({description, shortDescription, ...stylesProps}) => {
    const [isExpanded, setIsExpanded] = useState(false)

    const handleReadMoreClick = useCallback(() => {
        setIsExpanded(true)
    })

    if (!shortDescription && !description) {
        return null
    }

    return (
        <Box {...stylesProps}>
            <Text textStyle="bodyLarge2" color="darkGray">
                {isExpanded ? description : shortDescription}
                {!isExpanded && (
                    <Button
                        variant="link"
                        textDecoration={'none'}
                        fontSize={'lg'}
                        display="inline-block"
                        marginLeft="5px"
                        onClick={handleReadMoreClick}
                    >
                        <FormattedMessage {...READ_MORE_LABEL} />
                    </Button>
                )}
            </Text>
            {/* Box content hidden for SEO purposes */}
            <Box display="none">
                <Text>{description}</Text>
            </Box>
        </Box>
    )
}

ReadMoreText.propTypes = {
    description: PropTypes.string.isRequired,
    shortDescription: PropTypes.string
}

export default ReadMoreText
